import { type ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { formatDurationShort } from '../../lib/format';
import type { ProgressState, ResourceAmounts } from '../../lib/game';
import { Badge } from '../primitives/Badge';
import { Button } from '../primitives/Button';
import { Countdown } from '../primitives/Countdown';
import { CostList } from './CostList';
import { ClockIcon, LockIcon, ResearchIcon } from '../../icons/ui-icons';

export interface TechNodeProps {
  name: ReactNode;
  /** The per-level effect, e.g. "+10 % Schildkapazität pro Stufe". */
  effect?: ReactNode;
  level: number;
  maxLevel?: number;
  state?: ProgressState;
  icon?: ReactNode;
  cost?: ResourceAmounts;
  available?: ResourceAmounts;
  /** Research duration in seconds. */
  duration?: number;
  /** Completion time while `state` is `active`. */
  finishesAt?: number | string | Date;
  requirement?: ReactNode;
  onResearch?: () => void;
  className?: string;
}

/**
 * A node in the research tree (GDD §1 technology catalogue).
 *
 * Laid out as a horizontal row rather than a card: research is read as a list
 * of upgrades with cumulative effects, and the per-level effect text is the
 * thing players actually compare — so it gets the prominent position, not the
 * artwork.
 */
export function TechNode({
  name,
  effect,
  level,
  maxLevel,
  state = 'available',
  icon,
  cost,
  available,
  duration,
  finishesAt,
  requirement,
  onResearch,
  className,
}: TechNodeProps) {
  const locked = state === 'locked';
  const active = state === 'active';
  const maxed = state === 'maxed';

  return (
    <div
      className={cn(
        'flex flex-col gap-3 rounded-panel border bg-plate p-3 sm:flex-row sm:items-center',
        active ? 'border-aether/45' : 'border-edge',
        locked && 'opacity-60',
        className,
      )}
    >
      {/* Hex-ish emblem plate — research reads as arcane relative to construction. */}
      <div
        className={cn(
          'relative flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-panel border',
          active
            ? 'border-aether/50 bg-aether/10 text-aether-bright shadow-glow-aether'
            : 'border-edge-subtle bg-surface-2 text-content-muted',
        )}
      >
        {locked ? <LockIcon size={20} /> : (icon ?? <ResearchIcon size={24} />)}
        <span
          data-sg-numeric
          className={cn(
            'absolute -bottom-1.5 -right-1.5 rounded-control border border-edge',
            'bg-surface-overlay px-1 font-display text-label-sm font-semibold text-content',
          )}
        >
          {level}
          {maxLevel !== undefined ? `/${maxLevel}` : ''}
        </span>
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-2">
          <h4 className="font-display text-xs font-semibold uppercase tracking-[0.1em] text-content">
            {name}
          </h4>
          {active ? (
            <Badge accent="aether" size="sm" pulse>
              Forschung läuft
            </Badge>
          ) : null}
          {maxed ? (
            <Badge accent="success" size="sm">
              Max. Stufe
            </Badge>
          ) : null}
        </div>

        {effect ? (
          <p className="mt-1 text-xs leading-relaxed text-content-secondary">{effect}</p>
        ) : null}

        {locked && requirement ? (
          <p className="mt-1 text-xs text-content-muted">{requirement}</p>
        ) : null}

        {cost && !locked && !maxed ? (
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
            <CostList cost={cost} available={available} />
            {duration !== undefined ? (
              <span className="inline-flex items-center gap-1 text-xs text-content-muted">
                <ClockIcon size={12} />
                <span data-sg-numeric>{formatDurationShort(duration)}</span>
              </span>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="shrink-0 sm:w-32 sm:text-right">
        {active && finishesAt ? (
          <Countdown target={finishesAt} urgentBelow={600} className="text-sm font-semibold" />
        ) : (
          <Button
            variant="primary"
            size="sm"
            fullWidth
            disabled={state !== 'available'}
            onClick={onResearch}
          >
            Erforschen
          </Button>
        )}
      </div>
    </div>
  );
}
